import axios from 'axios';
import LocalStorageServices from './helpers/LocalStorageServices';
import { SERVER_URL } from './serverUrl';

const api = axios.create({
  baseURL: SERVER_URL,
  timeout: 60 * 1000, // 1 minute
});

api.interceptors.request.use(
  (params) => {
    const token = LocalStorageServices.getToken();
    if (token) {
      params.headers.Authorization = `Bearer ${token}`;
    }
    return params;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    if (error?.response?.status === 401) {
      LocalStorageServices.removeToken();
    }
    return Promise.reject(error);
  },
);

class Api {
  static url = SERVER_URL;

  /* Account start */

  static login(data) {
    return api.post('/api/admins/login', data);
  }

  static getProfile(data) {
    return api.get('/api/admins/profile', data);
  }

  /* Account end */

  /* Organizations start */

  static getOrganizations(params, config = {}) {
    return api.get('/api/organizations', { params, ...config });
  }

  static searchOrganizations(params, config = {}) {
    return api.get('/api/organizations/search', { params, ...config });
  }

  static getOrganization({ id }) {
    return api.get(`/api/organizations/${id}?should_add_clicks=false`);
  }

  static blockOrganization(data) {
    return api.put('/api/admins/organizations/block', data);
  }

  static deleteOrganization(data) {
    return api.post('/api/admins/organizations', data);
  }

  static changeOrganizationOwner(data) {
    return api.put('/api/admins/organizations/change-owner', data);
  }

  static approveOrganizations(data) {
    return api.put('/api/admins/organizations/approve', data);
  }

  static unapproveOrganizations(data) {
    return api.put('/api/admins/organizations/unapprove', data);
  }

  static createOrganization(formData) {
    return api.post('/api/admins/organizations/create', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  static editOrganization({ id, payload }) {
    return api.patch(`/api/admins/organizations/edit/${id}`, payload);
  }

  static addOrganizationPhotos({ id, formData }) {
    return api.post(`/api/admins/organizations/${id}/photos/add`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  static removeOrganizationPhotos({ id, payload }) {
    return api.post(`/api/admins/organizations/${id}/photos/remove`, payload);
  }

  /* Organizations end */

  /* Events start */

  static getEvents(params, config = {}) {
    return api.get('/api/events', { params, ...config });
  }

  static searchEvents(params, config = {}) {
    return api.get('/api/events/search', { params, ...config });
  }

  static getAdminEvents(params, config = {}) {
    return api.get('/api/admins/events', { params, ...config });
  }

  static getEvent({ id }) {
    return api.get(`/api/events/${id}?should_add_clicks=false`);
  }

  static blockEvent(data) {
    return api.put('/api/admins/events/block', data);
  }

  static deleteEvent({ id }) {
    return api.delete(`/api/admins/events/${id}`);
  }

  static createEvent(formData) {
    return api.post('/api/admins/events/create', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  static editEvent({ id, payload }) {
    return api.patch(`/api/admins/events/edit/${id}`, payload);
  }

  static addEventPhotos({ id, formData }) {
    return api.post(`/api/admins/events/${id}/photos/add`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  static removeEventPhotos({ id, payload }) {
    return api.post(`/api/admins/events/${id}/photos/remove`, payload);
  }

  static getAdminEventsPendingCategory() {
    return api.get('/api/admins/events', { params: { categories: 'none', is_active: 'all' } });
  }

  static bulkCategoryPublishEvents(payload) {
    return api.post('/api/admins/events/bulk-category-publish', payload);
  }

  static getEventCategories() {
    return api.get('/api/events/categories');
  }

  static getOtherEventCategory() {
    return api.get('/api/events/other-category');
  }

  /* Events end */

  /* Users start */
  static getUsers(data) {
    return api.get('/api/admins/users', {
      params: data,
    });
  }

  static blockUser(data) {
    return api.put('/api/admins/users/block', data);
  }

  static deleteUser({ id }) {
    return api.delete(`/api/admins/users/${id}`);
  }

  /* Users end */

  /* Admins start */

  static getAdmins(data) {
    return api.get('/api/root/admins', {
      params: data,
    });
  }

  static editAdmin({ id, data }) {
    return api.put(`/api/root/admins/${id}`, data);
  }

  static addAdmin(data) {
    return api.post('/api/root/admins', data);
  }

  static deleteAdmin(id) {
    return api.delete(`/api/root/admins/${id}`);
  }

  static getAdminAccess() {
    return api.get('/api/root/admin-access');
  }

  /* Admins end */

  /* Static data start */

  static getCountries() {
    return api.get('/api/static-data/countries').then((response) => {
      if (response?.data?.countries) {
        response.data.countries = [...response.data.countries].sort(
          (a, b) => (a.name || '').localeCompare(b.name || '', 'ru'),
        );
      }
      return response;
    });
  }

  static getLanguages() {
    return api.get('/api/static-data/languages');
  }

  static getCategories() {
    return api.get('/api/static-data/categories-all');
  }

  static getCities(params) {
    return api.get('/api/static-data/cities', { params });
  }

  static getOnlineCity() {
    return api.get('/api/static-data/online-city');
  }

  static getInfoTable(params) {
    return api.get('/api/static-data/info-table', { params });
  }

  /* Static data end */

  /* Admins start */

  static addCountry(formData) {
    return api.post('/api/admins/countries', formData);
  }

  static editCountry({ id, formData }) {
    return api.put(`/api/admins/countries/${id}`, formData);
  }

  static deleteCountry(id) {
    return api.delete(`/api/admins/countries/${id}`);
  }

  static addCity(data) {
    return api.post('/api/admins/cities', data);
  }

  static editCities(data) {
    return api.put('/api/admins/cities', { ...data, lala: 1111 });
  }

  static deleteCity(id) {
    return api.delete(`/api/admins/cities/${id}`);
  }

  static getCountryKeywords(id) {
    return api.get(`/api/admins/countries/${id}/keywords`);
  }

  static putCountryKeywords({ id, keywords }) {
    return api.put(`/api/admins/countries/${id}/keywords`, { keywords });
  }

  static getCityKeywords(id) {
    return api.get(`/api/admins/cities/${id}/keywords`);
  }

  static putCityKeywords({ id, keywords }) {
    return api.put(`/api/admins/cities/${id}/keywords`, { keywords });
  }

  /* Admins end */

  /* Categories start */

  static getAdminCategories(params) {
    return api.get('/api/admins/categories', { params });
  }

  static createAdminCategory(body) {
    return api.post('/api/admins/categories', body);
  }

  static renameAdminCategory({ id, ...body }) {
    return api.put(`/api/admins/categories/${id}/name`, body);
  }

  static editSortAdminCategory(body) {
    return api.put('/api/admins/categories/sort', body);
  }

  static editIconAdminCategory({ id, formData, params }) {
    return api.put(`/api/admins/categories/${id}/icon`, formData, { params });
  }

  static deleteAdminCategory({ id }) {
    return api.delete(`/api/admins/categories/${id}`);
  }

  static getAdminSubCategories(params) {
    return api.get('/api/admins/sub-categories', { params });
  }

  static createAdminSubCategory(body) {
    return api.post('/api/admins/sub-categories', body);
  }

  static renameAdminSubCategory({ id, ...body }) {
    return api.put(`/api/admins/sub-categories/${id}/name`, body);
  }

  static editSortAdminSubCategory(body) {
    return api.put('/api/admins/sub-categories/sort', body);
  }

  static deleteAdminSubCategory({ id }) {
    return api.delete(`/api/admins/sub-categories/${id}`);
  }

  /* Categories end */

  /* Articles start */

  static getArticleBlocks(params) {
    return api.get('/api/admins/article-blocks', { params });
  }

  static createArticleBlock(body) {
    return api.post('/api/admins/article-blocks', body);
  }

  static renameArticleBlock({ id, ...body }) {
    return api.put(`/api/admins/article-blocks/${id}/name`, body);
  }

  static editSortArticleBlocks(body) {
    return api.put('/api/admins/article-blocks/sort', body);
  }

  static deleteArticleBlock({ id, ...params }) {
    return api.delete(`/api/admins/article-blocks/${id}`, { params });
  }

  static getArticles(params) {
    return api.get('/api/admins/articles', { params });
  }

  static createArticle({ formData, ...params }) {
    return api.post('/api/admins/articles', formData, { params });
  }

  static editArticle({ formData, id, ...params }) {
    return api.put(`/api/admins/articles/${id}`, formData, { params });
  }

  static deleteArticle({ id, ...params }) {
    return api.delete(`/api/admins/articles/${id}`, { params });
  }

  /* Articles end */

  /* Gallery start */

  static getGalleryPhotos(params) {
    return api.get('/api/admins/gallery', { params });
  }

  static addPhotosToGallery(formData) {
    return api.post('/api/admins/gallery', formData);
  }

  static deletePhotosToGallery({ id, params }) {
    return api.delete(`/api/admins/gallery/${id}`, { params });
  }

  /* Gallery end */

  /* TemplatePhotos start */

  static getTemplatePhotosPhotos(params) {
    return api.get('/api/admins/template-photos', { params });
  }

  static getSingleTemplatePhoto({ id }) {
    return api.get(`/api/admins/template-photos/${id}`);
  }

  static addPhotosToTemplatePhotos(formData) {
    return api.post('/api/admins/template-photos', formData);
  }

  static deletePhotosToTemplatePhotos({ id, params }) {
    return api.delete(`/api/admins/template-photos/${id}`, { params });
  }

  /* TemplatePhotos end */

  /* Infos start */

  static editInfoItem(data) {
    return api.put('/api/admins/infos', data);
  }

  /* Infos end */

  /* Complaints start */

  static getComplaints({ params }) {
    return api.get('/api/admins/complaints', { params });
  }

  static getComplaintsCount({ params }) {
    return api.get('/api/admins/complaints/count', { params });
  }

  static answerComplain(data) {
    return api.put('/api/admins/complaints', data);
  }

  /* Complaints end */

  /* Geocoding */
  static getDropdownAddresses({ params, signal }) {
    return api.get('/api/admins/geocoding/complete-address', { params, signal });
  }

  static getAddressCoordinates({ params }) {
    return api.get('/api/admins/geocoding/get-place-coordinates', { params });
  }

  static getPlaceNameWithCoordinates({ params }) {
    return api.get('/api/admins/geocoding/get-place-name-with-coordinates', { params });
  }
  /* Geocoding */

  /* Operations start */

  static getOperations(params) {
    return api.get('/api/operations', { params });
  }

  static getOperation({ id }) {
    return api.get(`/api/operations/${id}`);
  }

  static callOperation(data) {
    return api.post('/api/operations/call', data);
  }

  static submitFientaHtml(data) {
    return api.post('/api/operations/submit-fienta-html', data);
  }

  /* Operations end */

  /* Notifications start */

  static sendGlobalNotification(data) {
    return api.post('/api/admins/notifications/send-global', data);
  }

  /* Notifications end */

  /* Translations start */

  static getTranslationsMeta() {
    return api.get('/api/translations/meta');
  }

  static getTranslationsStats() {
    return api.get('/api/translations/stats');
  }

  static getTranslationsUiStrings() {
    return api.get('/api/translations/ui-strings');
  }

  static getTranslationsList({ entityType, ...params }) {
    return api.get(`/api/translations/${entityType}`, { params });
  }

  static getTranslation({ entityType, entityId }) {
    return api.get(`/api/translations/${entityType}/${entityId}`);
  }

  static putTranslations({ entityType, entityId, translations }) {
    return api.put(`/api/translations/${entityType}/${entityId}`, { translations });
  }

  static deleteTranslations({ entityType, entityId, ...params }) {
    return api.delete(`/api/translations/${entityType}/${entityId}`, { params });
  }

  /* Translations end */

  /* Payments start */

  static getPayments(params) {
    return api.get('/api/admins/payments', { params });
  }

  static getPayment({ id }) {
    return api.get(`/api/admins/payments/${id}`);
  }

  static getPaymentSettings(params) {
    return api.get('/api/admins/payments/settings', { params });
  }

  static updatePaymentSetting({ id, ...data }) {
    return api.put(`/api/admins/payments/settings/${id}`, data);
  }

  static createOrUpdatePaymentSettingSlot(data) {
    return api.post('/api/admins/payments/settings', data);
  }

  static deletePaymentSetting(id) {
    return api.delete(`/api/admins/payments/settings/${id}`);
  }

  /* Payments end */

  static getScreenTimeStats(params) {
    return api.get('/api/admins/screen-time/stats', { params });
  }

  static getOpenAiUsage(params) {
    return api.get('/api/admins/openai-usage', { params });
  }
}

export default Api;
